import { clearToken, getToken } from "./token"

// Em produção, o frontend e o backend ficam em domínios diferentes no
// Hostinger, então API_BASE precisa ser a URL completa da API.
// Em desenvolvimento local, continua "/api" (funciona com o proxy do CRA).
const API_BASE = process.env.REACT_APP_API_URL || "/api"

async function apiRequest(path, options) {
  const token = getToken()
  const headers = {
    ...(options?.headers || {}),
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  })

  let data = null
  try {
    data = await res.json()
  } catch {
    // ignore
  }

  if (res.status === 401) {
    clearToken()
  }

  if (!res.ok) {
    throw new Error(data?.message || "Falha na requisição.")
  }

  return data
}

export async function login({ email, password, role }) {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, role }),
  })

  let data = null
  try {
    data = await res.json()
  } catch {
    // ignore
  }

  if (!res.ok) {
    throw new Error(data?.message || "Falha no login.")
  }

  if (!data?.token) {
    throw new Error("Resposta inválida do servidor.")
  }

  return data
}

export async function getMe() {
  const data = await apiRequest("/auth/me", { method: "GET" })
  return data?.user
}

export async function getProfessorDashboard() {
  return apiRequest("/professor/dashboard", { method: "GET" })
}

export async function getStudentDashboard() {
  return apiRequest("/student/dashboard", { method: "GET" })
}

export async function getGraduacoes() {
  const data = await apiRequest("/graduacoes", { method: "GET" })
  return data?.graduacoes || []
}

export async function registerStudent(payload) {
  const res = await fetch(`${API_BASE}/auth/register/student`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  })

  let data = null
  try {
    data = await res.json()
  } catch {
    // ignore
  }

  if (!res.ok) {
    throw new Error(data?.message || "Falha no cadastro.")
  }

  if (!data?.token) {
    throw new Error("Resposta inválida do servidor.")
  }

  return data
}

// Atualiza os dados do próprio usuário logado (funciona para professor e aluno)
export async function updateProfile(payload) {
  return apiRequest("/profile", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  })
}

// Aluno troca a própria foto de perfil (envia multipart/form-data)
export async function uploadProfilePhoto(file) {
  const formData = new FormData()
  formData.append("photo", file)
  return apiRequest("/profile/photo", {
    method: "POST",
    body: formData,
  })
}

// Altera a senha do próprio usuário logado (funciona para professor e aluno)
export async function changePassword({ senhaAtual, novaSenha }) {
  return apiRequest("/password", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ senhaAtual, novaSenha }),
  })
}

// Aluno registra a própria presença (tela acessada via QR code da academia)
export async function registerAttendance(token) {
  return apiRequest("/student/attendance", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  })
}

// Professor consulta a lista de alunos presentes em um dia (YYYY-MM-DD)
export async function getAttendanceByDate(date) {
  const data = await apiRequest(`/professor/attendance?date=${date}`, {
    method: "GET",
  })
  return data?.attendance || []
}

// Professor consulta o resumo de presenças de um mês (YYYY-MM), para o calendário
export async function getAttendanceSummary(month) {
  const data = await apiRequest(`/professor/attendance/summary?month=${month}`, {
    method: "GET",
  })
  return data?.summary || []
}

// Professor consulta o histórico de presenças de um aluno em um ano
export async function getStudentAttendance(studentId, year) {
  const y = year || new Date().getFullYear()
  const data = await apiRequest(`/professor/students/${studentId}/attendance?year=${y}`, {
    method: "GET",
  })
  return data || { total: 0, dates: [] }
}

// Aluno visualiza o próprio resumo de presenças do mês (para o calendário do StudentDashboard)
export async function getStudentAttendanceSummary(month) {
  const data = await apiRequest(`/student/attendance/summary?month=${month}`, {
    method: "GET",
  })
  return data?.attendance || []
}

// Aluno verifica se esteve presente em um dia específico
export async function getStudentAttendanceByDate(date) {
  const data = await apiRequest(`/student/attendance/date?date=${date}`, {
    method: "GET",
  })
  return data || { present: false, horario: null }
}

// Esqueci a senha — solicita link de redefinição por e-mail
export async function requestPasswordReset(email) {
  const res = await fetch(`${API_BASE}/auth/password-reset/request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  })
  let data = null
  try { data = await res.json() } catch { /* ignore */ }
  if (!res.ok) throw new Error(data?.message || "Falha ao solicitar redefinição.")
  return data
}

// Valida se o token de reset é válido e retorna info do usuário
export async function validateResetToken(token) {
  const res = await fetch(`${API_BASE}/auth/password-reset/validate?token=${encodeURIComponent(token)}`, {
    method: "GET",
  })
  let data = null
  try { data = await res.json() } catch { /* ignore */ }
  if (!res.ok) throw new Error(data?.message || "Token inválido.")
  return data
}

// Redefine a senha usando o token recebido por e-mail
export async function resetPasswordWithToken({ token, novaSenha }) {
  const res = await fetch(`${API_BASE}/auth/password-reset/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, novaSenha }),
  })
  let data = null
  try { data = await res.json() } catch { /* ignore */ }
  if (!res.ok) throw new Error(data?.message || "Falha ao redefinir senha.")
  return data
}

// Gestão de alunos pelo professor
export async function getStudents() {
  const data = await apiRequest("/professor/students", { method: "GET" })
  return data?.students || []
}

export async function updateStudentByProfessor(studentId, payload) {
  return apiRequest(`/professor/students/${studentId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  })
}

export async function deleteStudent(studentId) {
  return apiRequest(`/professor/students/${studentId}`, {
    method: "DELETE",
  })
}
